import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/app/Header";
import { useAuth } from "@/context/AuthContext";
import { api, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Camera, TrendingUp, AlertCircle } from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [scans, setScans] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const loadScans = async () => {
    try {
      const r = await api.get("/scans");
      setScans(r.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadScans();
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(file.type)) {
      toast.error("Only JPEG, PNG, or WEBP images allowed");
      return;
    }
    if ((user?.scans_remaining || 0) <= 0) {
      toast.error("No scans left — grab a pack");
      nav("/pricing");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/scans", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Scan complete");
      nav(`/scans/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const token = getToken();

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#10b981] mb-3">/ Control Room</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
              Hey, {user?.name || "flipper"}.
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-4 md:min-w-[360px]">
            <StatBox label="Scans Remaining" value={user?.scans_remaining ?? 0} accent="#10b981" testid="stat-remaining" />
            <StatBox label="Total Scans" value={user?.total_scans_used ?? 0} accent="#fafafa" testid="stat-total" />
          </div>
        </div>

        {/* Uploader */}
        <div className="grid grid-cols-12 gap-4 mb-16">
          <div
            className="col-span-12 lg:col-span-8 border border-white/8 bg-[#0f1512] p-8 md:p-12 min-h-[420px] flex flex-col items-center justify-center text-center relative overflow-hidden"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="upload-dropzone"
          >
            {uploading && preview ? (
              <div className="relative w-full max-w-md">
                <img src={preview} alt="scanning" className="w-full h-80 object-cover" />
                <div className="absolute inset-0 overflow-hidden">
                  <div className="scan-laser" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-black/80 backdrop-blur">
                  <p className="font-mono text-xs uppercase tracking-widest text-[#10b981] mb-1">/ Scanning</p>
                  <p className="text-sm text-zinc-300">Analyzing brand · condition · market value…</p>
                </div>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 border-2 border-[#10b981] flex items-center justify-center mb-6">
                  <Upload className="w-8 h-8 text-[#10b981]" />
                </div>
                <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-3">
                  Drop a photo. Get a Profit Report.
                </h2>
                <p className="text-zinc-400 mb-8 max-w-md">
                  JPEG · PNG · WEBP up to 10 MB. Well-lit, clear shot of the item works best.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => inputRef.current?.click()}
                    className="rounded-lg bg-[#10b981] hover:bg-[#059669] text-white font-semibold h-12 px-6"
                    data-testid="upload-choose-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Choose Photo
                  </Button>
                  <Button
                    onClick={() => inputRef.current?.click()}
                    variant="outline"
                    className="rounded-lg border-white/20 bg-transparent hover:bg-white/5 font-semibold h-12 px-6"
                    data-testid="upload-camera-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" /> Take Photo
                  </Button>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="upload-input"
                />
              </>
            )}
          </div>

          <div className="col-span-12 lg:col-span-4 grid grid-cols-1 gap-4">
            <QuickAction
              icon={<TrendingUp className="w-5 h-5" />}
              title="Buy a Scan Pack"
              body="20 / 120 / 1000 scans"
              cta="View Packs"
              onClick={() => nav("/pricing")}
              testid="dashboard-buy-pack"
            />
            <QuickAction
              icon={<AlertCircle className="w-5 h-5" />}
              title="Scan Tips"
              body="Shoot from waist-up. Include tags & logos. Skip flash."
            />
          </div>
        </div>

        {/* History */}
        <div>
          <div className="flex justify-between items-end mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#10b981] mb-3">/ History</p>
              <h2 className="font-display font-bold text-3xl tracking-tight">Recent scans</h2>
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
              {scans.length} total
            </div>
          </div>

          {scans.length === 0 ? (
            <div className="border border-dashed border-white/8 py-16 text-center text-zinc-500">
              No scans yet. Drop a photo above.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="scan-history-grid">
              {scans.map((s) => (
                <Link
                  key={s.id}
                  to={`/scans/${s.id}`}
                  className="group border border-white/8 bg-[#0f1512] hover:border-white/30 transition-all"
                  data-testid={`scan-card-${s.id}`}
                >
                  <div className="aspect-square bg-[#0a0f0d] overflow-hidden relative">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${s.image_url}?auth=${token}`}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="font-display font-bold text-lg tracking-tight truncate">
                      {s.report?.item_name || "Item"}
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                      <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                        {s.report?.brand || "—"}
                      </span>
                      <span className="font-mono text-sm text-[#10b981] font-bold">
                        ${s.report?.estimated_value?.low ?? 0}–${s.report?.estimated_value?.high ?? 0}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent, testid }) {
  return (
    <div className="border border-white/8 bg-[#0f1512] p-5" data-testid={testid}>
      <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-bold text-4xl mt-1" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function QuickAction({ icon, title, body, cta, onClick, testid }) {
  return (
    <div className="border border-white/8 bg-[#0f1512] p-6 flex flex-col">
      <div className="w-10 h-10 border border-white/20 flex items-center justify-center text-[#10b981] mb-4">
        {icon}
      </div>
      <div className="font-display font-bold text-xl tracking-tight">{title}</div>
      <p className="text-sm text-zinc-400 mt-1 flex-1">{body}</p>
      {cta && (
        <Button
          onClick={onClick}
          size="sm"
          className="mt-4 self-start rounded-lg bg-transparent border border-white/20 hover:bg-[#10b981] hover:border-[#10b981] font-semibold text-xs"
          data-testid={testid}
        >
          {cta}
        </Button>
      )}
    </div>
  );
}
